import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircleCheck as CheckCircle, Loader as Loader2, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

export const SuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      if (!user || !sessionId) {
        setLoading(false);
        return;
      }

      try {
        // Wait a moment for webhook to process
        await new Promise(resolve => setTimeout(resolve, 2000));

        const { data, error } = await supabase
          .from('stripe_user_subscriptions')
          .select('*')
          .single();

        if (error) {
          console.error('Error fetching subscription:', error);
        } else {
          setSubscription(data);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionDetails();
  }, [user, sessionId]);

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Invalid Session</h1>
          <p className="text-gray-600 mb-8">This success page requires a valid session ID.</p>
          <button
            onClick={() => navigate('/pricing')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            View Pricing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {loading ? (
          <>
            <Loader2 className="h-16 w-16 text-blue-600 animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Processing Your Subscription
            </h1>
            <p className="text-gray-600">
              Please wait while we set up your account...
            </p>
          </>
        ) : (
          <>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Welcome to Social Pack Generator!
            </h1>
            <p className="text-gray-600 mb-6">
              Your subscription has been successfully activated. You can now start generating amazing images for your social media.
            </p>

            {subscription && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h2 className="font-semibold text-gray-900 mb-2">Subscription Details</h2>
                <p className="text-sm text-gray-600">
                  Status: <span className="font-medium text-green-600 capitalize">
                    {subscription.subscription_status}
                  </span>
                </p>
                {subscription.price_id && (
                  <p className="text-sm text-gray-600">
                    Plan: <span className="font-medium">
                      {subscription.price_id === 'price_1SNzsHJGYRa8WI9FykTvTg8z' && 'Starter Plan'}
                      {subscription.price_id === 'price_1SPVbKJGYRa8WI9Fm7KtEpdM' && 'Basic Plan'}
                      {subscription.price_id === 'price_1SPVc3JGYRa8WI9FQ1LjavEx' && 'Pro Plan'}
                      {subscription.price_id === 'price_1SPVcbJGYRa8WI9FyV6vbgha' && 'Unlimited Plan'}
                    </span>
                  </p>
                )}
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={() => navigate('/generator')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                Start Generating Images
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>

              <button
                onClick={() => navigate('/account')}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Go to Account
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};